import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import useProfile from '../../customHooks/useProfile'
import useAuthentication from '../../customHooks/useAuthentication'

export function useCrossPageAssessmentMethods() {
  
  const navigate = useNavigate()
  const profileStatus: UserProfileStatus = useSelector((state: ProfileState) => state.profile)
  const [profileStatusData, setProfileStatusData] = useState<UserProfileStatus>(profileStatus)
  const { getUserProfileStatus } = useProfile()
  useEffect(() => getUserProfileStatus(setProfileStatusData), [])
  
  const [profileData, setProfileData] = useState<UserProfile>()
  const { getUserInformation } = useAuthentication()
  useEffect(() => getUserInformation(setProfileData), [])
  
  const getAllAssessments = (): AssessmentProps[] => {
    // These flags need to be set according to the actual test.
    let cyberAssessmentDateIfReal: string | null = (profileStatusData && profileStatusData.cyberAssessmentTakenOn) ? profileStatusData.cyberAssessmentTakenOn! : null;
    let cyberAssessmentMessage: string | null = (profileStatusData && profileStatusData.cyberAssessmentMessage) ? profileStatusData.cyberAssessmentMessage! : null;
    let requireDocumentReview: boolean = !profileData || profileData.attorney;
    let requireCyber: boolean = !profileData || !profileData.attorney;
    let docReviewAssessments = profileStatusData?.docReviewAssessments || {};

    return [
      {
        name: 'Document Review Skills Assessment',
        languages: [
          {
            language: 'English', 
            val: 'en', 
            takenAt: docReviewAssessments?.['en']?.assessment_taken_on || null, 
            required: true, 
            assessmentMessage: docReviewAssessments?.['en']?.assessment_message || '',
            addedAt: null,
            onStartTestClickMethod: () => navigate('/assessment/document_review/begin', {state: { language: 'en' }}),
            onInstructionsClickMethod: () => navigate('/assessment/document_review/instructions', {state: { language: 'en' }}),
            onPracticeTestClickMethod: () => navigate('/assessment/document_review/practice', {state: { language: 'en' }})
          },
          {
            language: 'Chinese (Mandarin)',
            val: 'zh_cmn',
            takenAt: docReviewAssessments?.['zh_cmn']?.assessment_taken_on || null,
            required: false,
            assessmentMessage: docReviewAssessments?.['zh_cmn']?.assessment_message || '',
            addedAt: null,
            onStartTestClickMethod: () => navigate('/assessment/document_review/begin', {state: { language: 'zh_cmn' }}),
            onInstructionsClickMethod: () => navigate('/assessment/document_review/instructions', {state: {language: 'zh_cmn'}}),
            onPracticeTestClickMethod: () => navigate('/assessment/document_review/practice', {state: {language: 'zh_cmn'}})
          },
          {
          language: 'Japanese',
          val: 'ja',
          takenAt: docReviewAssessments?.['ja']?.assessment_taken_on || null,
          required: false,
          assessmentMessage: docReviewAssessments?.['ja']?.assessment_message || '',
          addedAt: null,
          onStartTestClickMethod: () => navigate('/assessment/document_review/begin', {state: { language: 'ja' }}),
          onInstructionsClickMethod: () => navigate('/assessment/document_review/instructions', {state: {language: 'ja'}}),
          onPracticeTestClickMethod: () => navigate('/assessment/document_review/practice', {state: {language: 'ja'}})
          }
        ],
        anyRequired: requireDocumentReview
      },
      {
        name: 'Cyber Skills Assessment',
        languages: [
          {
            language: 'English', 
            val: 'en', 
            takenAt: cyberAssessmentDateIfReal,
            required: true,
            assessmentMessage: cyberAssessmentMessage,
            addedAt: null, 
            onStartTestClickMethod: () => navigate('/assessment/cyber/begin'),
            onInstructionsClickMethod: () => navigate('/assessment/cyber/instructions')
          }
        ],
        anyRequired: requireCyber
      }
    ];
  }
  
  const getRequiredAssessments = (): RequiredAssessmentProps[] => 
    getAllAssessments().filter(assessmentProps => assessmentProps.anyRequired)
      .map((assessmentProps: AssessmentProps): RequiredAssessmentProps => 
        ({
          name: assessmentProps.name,
          languages: assessmentProps.languages.map((languageProps: AssessmentSingleLanguageProps): RequiredAssessmentSingleLanguageProps => 
            ({
              language: languageProps.language,
              val: languageProps.val,
              takenAt: languageProps.takenAt,
              required: languageProps.required,
              assessmentMessage: languageProps.assessmentMessage,
              onStartTestClickMethod: languageProps.onStartTestClickMethod,
              onInstructionsClickMethod: languageProps.onInstructionsClickMethod,
              onPracticeTestClickMethod: languageProps.onPracticeTestClickMethod
            })),
        }));
  
  const getOtherAssessments = (): OtherAssessmentProps[] => 
    getAllAssessments().filter(assessmentProps => !assessmentProps.anyRequired)
      .map((assessmentProps: AssessmentProps): OtherAssessmentProps => 
        ({
          name: assessmentProps.name,
          languages: assessmentProps.languages.map((languageProps: AssessmentSingleLanguageProps): OtherAssessmentSingleLanguageProps => 
            ({
              language: languageProps.language,
              val: languageProps.val,
              takenAt: languageProps.takenAt,
              addedAt: languageProps.addedAt ?? '',
              assessmentMessage: languageProps.assessmentMessage,
              onStartTestClickMethod: languageProps.onStartTestClickMethod,
              onInstructionsClickMethod: languageProps.onInstructionsClickMethod,
              onPracticeTestClickMethod: languageProps.onPracticeTestClickMethod
            })),
        }));
  
  // const getRequiredAssessments = (): RequiredAssessmentProps[] => {
  //   return [
  //     {
  //       name: 'Document Review Skills Assessment',
  //       languages: [
  //         {language: 'English', val: 'en', takenAt: '2025-03-07T12:13', required: true, assessmentMessage: "Here's a really long message and make sure you pay attention to how the line wraps", onStartTestClickMethod: () => navigate('/assessment/document_review/begin'), onInstructionsClickMethod: () => {}, onPracticeTestClickMethod: () => {}}, 
  //         {language: "This one's optional", val: 'op', takenAt: '2025-05-12T12:34', required: false, assessmentMessage: "Again, a really long message but the text is slightly different in this one", onInstructionsClickMethod: () => navigate('/assessment/document_review/instructions'), onStartTestClickMethod: () => {}, onPracticeTestClickMethod: () => {}},
  //         {language: 'Another one', val: 'ao', takenAt: '2025-03-08T10:03', required: true, assessmentMessage: "Oh, did you think I forgot about this one? Nope, yet more placeholder text to ponder", onPracticeTestClickMethod: () => navigate('/assessment/document_review/practice')}, 
  //         {language: "Second optional language", val: 'so', takenAt: null, required: false}
  //       ]
  //     },
  //     {
  //       name: 'Cyber Skills Assessment',
  //       languages: [
  //         {language: 'English', val: 'en', takenAt: null, required: true, onStartTestClickMethod: () => navigate('/assessment/cyber/begin')}, 
  //         {language: "Greek", val: 'gr', takenAt: null, required: false, onInstructionsClickMethod: () => navigate('/assessment/cyber/instructions')},
  //         {language: 'Spanish', val: 'ao', takenAt: null, required: true, onPracticeTestClickMethod: () => navigate('/assessment/cyber/practice')}, 
  //         {language: "Tagalog", val: 'ta', takenAt: '2025-08-29T14:27', required: false}
  //       ]
  //     }
  //   ];
  // }
  
  // const getOtherAssessments = (): OtherAssessmentProps[] => {
  //   return [
  //     {
  //       name: 'Assessment 3 with a longer name than previously',
  //       languages: [
  //         {language: 'English', val: 'en', takenAt: '2025-06-05T12:13', addedAt: '2025-05-01', assessmentMessage: "Here's a really long message and make sure you pay attention to how the line wraps", onStartTestClickMethod: () => navigate('/assessment/document_review/begin'), onInstructionsClickMethod: () => {}, onPracticeTestClickMethod: () => {}}, 
  //         {language: "Spanish", val: 'es', takenAt: null, addedAt: '2025-08-14', onInstructionsClickMethod: () => navigate('/assessment/document_review/instructions')},
  //         {language: 'Thai', val: 'th', takenAt: '2025-03-08T10:03', addedAt: '2025-09-03', onPracticeTestClickMethod: () => navigate('/assessment/document_review/practice')}, 
  //         {language: "Inuit", val: 'np', takenAt: null, addedAt: '2025-10-09'}
  //       ]
  //     },
  //     {
  //       name: 'Assessment 4 with a longer name than previously',
  //       languages: [
  //         {language: 'English', val: 'en', takenAt: null, addedAt: '2025-08-02', onStartTestClickMethod: () => navigate('/assessment/document_review/begin')}, 
  //         {language: "Spanish", val: 'es', takenAt: null, addedAt: '2025-10-12', onInstructionsClickMethod: () => navigate('/assessment/document_review/instructions')},
  //         {language: 'Esperanto', val: 'eo', takenAt: null, addedAt: '2026-06-08', onPracticeTestClickMethod: () => navigate('/assessment/document_review/practice')}, 
  //         {language: "Typescript", val: 'ts', takenAt: '2046-08-29T14:27', addedAt: '2044-04-01'}
  //       ]
  //     }
  //   ];
  // }
  
  return { getRequiredAssessments, getOtherAssessments }
}
